import React, { FC, useEffect, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { useCookies } from 'next-client-cookies';
import toast from 'react-hot-toast';
import { useTranslations } from 'use-intl';
import Modal from 'components/commercetools-ui/organisms/modal';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import { clearCustomizationData } from 'helpers/utils/customizationStorage';
import { getRegionalDomain } from 'helpers/utils/domain-mapping';
import { supportedCountries } from 'project.config';
import { useProjectSettings } from 'frontastic';
import { sdk } from 'sdk';

interface Country {
  name: string;
  code: string;
}

export interface CountrySwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CountrySwitchModal: FC<CountrySwitchModalProps> = ({ isOpen, onClose }) => {
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { projectSettings } = useProjectSettings();
  const translate = useTranslations();
  const [query, setQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedCountry(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const filteredCountries =
    query === ''
      ? supportedCountries
      : supportedCountries.filter((country) => country.name.toLowerCase().includes(query.toLowerCase()));

  const handleSelect = (country: Country | null) => {
    if (!country || isLoading) return;
    setSelectedCountry(country);
  };

  const handleConfirm = async () => {
    if (!selectedCountry || isLoading) return;

    const countryCode = selectedCountry.code;
    setIsLoading(true);
    try {
      const domain = getRegionalDomain(countryCode, projectSettings ?? {});
      if (!domain) {
        toast.error(translate('customer-support.country-switch-not-configured'));
        setIsLoading(false);
        return;
      }

      trackMenuNavigation('Header My Account', 'Switch Country', domain);

      const response = await sdk.composableCommerce.account.generateCountrySwitchOtp({
        country: countryCode,
        language: locale,
      });

      if (response.isError) {
        toast.error(translate('customer-support.country-switch-error'));
        setIsLoading(false);
        return;
      }

      const redirectUrl = new URL(`/printstore/${countryCode.toLowerCase()}`, domain);
      redirectUrl.searchParams.set('token', response.data.otp);
      redirectUrl.searchParams.set('gl', countryCode);
      redirectUrl.searchParams.set('hl', locale);

      clearCustomizationData();
      window.sessionStorage.removeItem('p2r-store-results');
      window.sessionStorage.removeItem('recentSearches');
      window.location.href = redirectUrl.toString();
    } catch {
      toast.error(translate('customer-support.country-switch-error'));
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        if (!isLoading) onClose();
      }}
      shouldCloseOnEsc={!isLoading}
      shouldCloseOnOverlayClick={!isLoading}
      preventScroll
      style={{ content: { width: 'calc(100% - 40px)', maxWidth: '25rem', padding: '24px' } }}
      overlayZIndex={500}
      aria-label={translate('customer-support.country-switch-title')}
    >
      <h2 className="mb-16 text-20 font-bold text-[#1f1f1f]">{translate('customer-support.country-switch-title')}</h2>

      <Combobox value={selectedCountry} onChange={handleSelect} disabled={isLoading}>
        <div className="relative">
          <ComboboxInput
            className="w-full rounded-lg border border-[#EBEBEB] px-12 py-10 text-16 text-[#1f1f1f] focus:border-[#018463] focus:outline-none"
            onChange={(e) => setQuery(e.target.value)}
            displayValue={(country: Country | null) => country?.name ?? ''}
            placeholder={translate('customer-support.country-switch-search')}
            autoFocus
            data-ffid="input-country-switch-search"
            aria-label={translate('customer-support.country-switch-search')}
          />
        </div>

        <ComboboxOptions
          static
          className="mt-8 max-h-[280px] overflow-y-auto overscroll-contain rounded-lg border border-[#EBEBEB]"
          data-ffid="list-country-switch-options"
        >
          {filteredCountries.length === 0 ? (
            <div className="px-12 py-10 text-14 text-[#5C5C5C]">{translate('customer-support.country-switch-no-results')}</div>
          ) : (
            filteredCountries.map((country) => (
              <ComboboxOption
                key={country.code}
                value={country}
                className={({ active }: { active: boolean }) =>
                  `cursor-pointer px-12 py-8 text-16 text-[#1f1f1f] ${active ? 'bg-[#F5F5F5]' : ''}`
                }
                data-ffid={`opt-country-switch-${country.code.toLowerCase()}`}
              >
                {country.name}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>

      {isLoading && (
        <div className="mt-16 flex items-center justify-center gap-8">
          <div className="h-20 w-20 animate-spin rounded-full border-2 border-[#018463] border-t-transparent" />
          <span className="text-14 text-[#5C5C5C]">{translate('customer-support.country-switch-loading')}</span>
        </div>
      )}

      <div className="mt-16 flex justify-end gap-8">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="rounded-full border border-[#018463] px-24 py-8 text-14 font-medium text-[#018463] hover:border-[#349D82] hover:text-[#349D82] disabled:opacity-50"
          data-ffid="btn-country-switch-cancel"
        >
          {translate('customer-support.country-switch-cancel')}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isLoading || !selectedCountry}
          className="rounded-full bg-[#018463] px-24 py-8 text-14 font-medium text-white hover:bg-[#349D82] disabled:opacity-50"
          data-ffid="btn-country-switch-confirm"
        >
          {translate('customer-support.country-switch-confirm')}
        </button>
      </div>
    </Modal>
  );
};

export default CountrySwitchModal;
